import React, { useState, useEffect } from 'react';
import {

  Box,
  Typography,
  useTheme,
  AppBar,
  Toolbar,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, HowToReg, People, Notifications, Shield, Speed, ViewCarousel } from '@mui/icons-material';
import { User } from 'firebase/auth';
import { accessControlService } from '@watchlist/shared';
import { nativeFirebase } from '../../services/nativeFirebaseService';
import { isCapacitorNative } from '../../utils/platformUtils';
import ResponsiveContainer from '../layouts/ResponsiveContainer';
import AccessRequestsTab from './AccessRequestsTab';
import UserManagementTab from './UserManagementTab';
import SendNotificationTab from './SendNotificationTab';
import PerformanceTab from './PerformanceTab';
import HomeStackCardsTab from './HomeStackCardsTab';
import { font } from '../../utils/fontHelper';

interface AdminConsoleProps {
  user: User;
  onBack: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const AdminConsole: React.FC<AdminConsoleProps> = ({ user, onBack }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === 'dark';

  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({
    totalUsers: 0,
    pendingRequests: 0,
    admins: 0,
    testUsers: 0,
    feedbackCount: 0,
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const accessStats = await accessControlService.getAccessStats();

        // Also get feedback count (platform-aware)
        let feedbackCount = 0;
        try {
          if (isCapacitorNative()) {
            const docs = await nativeFirebase.queryDocuments(
              'feedback',
              [{ field: 'status', operator: '==', value: 'new' }]
            );
            feedbackCount = docs.length;
          } else {
            const { collection, query, where, getDocs, getDb } = await import('@watchlist/shared');
            const feedbackRef = collection(getDb(), 'feedback');
            const newFeedbackQuery = query(feedbackRef, where('status', '==', 'new'));
            const feedbackSnapshot = await getDocs(newFeedbackQuery);
            feedbackCount = feedbackSnapshot.size;
          }
        } catch (err) {
          console.error('Error loading feedback count:', err);
        }

        setStats({ ...accessStats, feedbackCount });
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    };
    loadStats();
  }, [activeTab]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      {/* App Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: isDark ? 'rgba(28, 28, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          pt: 'var(--safe-area-inset-top)',
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontFamily: font(),
              flex: 1,
            }}
          >
            Admin Console
          </Typography>
          <IconButton
            edge="end"
            aria-label="Moderator console"
            onClick={() => navigate('/moderator')}
            sx={{ color: theme.palette.primary.main }}
          >
            <Shield />
          </IconButton>
        </Toolbar>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: 14,
              minHeight: 48,
            },
            '& .Mui-selected': {
              color: theme.palette.primary.main,
            },
          }}
        >
          <Tab
            icon={<HowToReg sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label={`Requests${stats.pendingRequests > 0 ? ` (${stats.pendingRequests})` : ''}`}
          />
          <Tab
            icon={<People sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label={`Users (${stats.totalUsers})`}
          />
          <Tab
            icon={<Notifications sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Notify"
          />
          <Tab
            icon={<Speed sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Performance"
          />
          <Tab
            icon={<ViewCarousel sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Home cards"
          />
        </Tabs>
      </AppBar>

      {/* Content */}
      <ResponsiveContainer maxWidth={800}>
        {/* Stats Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 2,
            mt: 3,
            mb: 2,
          }}
        >
          <StatCard
            label="Total Users"
            value={stats.totalUsers}
            color={theme.palette.primary.main}
            isDark={isDark}
          />
          <StatCard
            label="Pending"
            value={stats.pendingRequests}
            color="#FF9F0A"
            isDark={isDark}
          />
          <StatCard
            label="Admins"
            value={stats.admins}
            color="#FF453A"
            isDark={isDark}
          />
          <StatCard
            label="Testers"
            value={stats.testUsers}
            color="#30D158"
            isDark={isDark}
          />
        </Box>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          <AccessRequestsTab adminUid={user.uid} />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <UserManagementTab adminUid={user.uid} />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <SendNotificationTab isDark={isDark} />
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          <PerformanceTab adminUid={user.uid} />
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          <HomeStackCardsTab adminUid={user.uid} />
        </TabPanel>
      </ResponsiveContainer>
    </Box>
  );
};

interface StatCardProps {
  label: string;
  value: number;
  color: string;
  isDark: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, isDark }) => (
  <Box
    sx={{
      bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
      borderRadius: 2,
      p: 2,
      textAlign: 'center',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
    }}
  >
    <Typography
      sx={{
        fontSize: 28,
        fontWeight: 700,
        color: color,
        lineHeight: 1,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: 12,
        color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)',
        mt: 0.5,
      }}
    >
      {label}
    </Typography>
  </Box>
);

export default AdminConsole;
